import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { BackgroundImage } from '@vc/data/backgroundCategories'

type SettingsTab = 'audio' | 'video' | 'advanced'

/**
 * On-disk shape.
 *
 * NEITHER background is here. They are session state, not settings — see the
 * `lobbyBackground` / `meetingBackground` fields below for why.
 */
type PersistedPrefs = {
  permanentBottomBar: boolean
  autoHideNames: boolean
  sideDrawerOverContent: boolean
  inMeetingNotifications: boolean
  inMeetingNotificationSounds: boolean
  autoPictureInPicture: boolean
  hideChatNotifications: boolean
  recordingContainer: RecordingContainer
  preferredLayout: PreferredLayout
}

/**
 * Container for a local recording.
 *
 * `mp4` takes the WebCodecs path, which muxes a real seekable file. `webm`
 * takes MediaRecorder, which only writes streaming containers — no Cues index
 * and no duration in the header, so Windows' built-in players often cannot seek
 * it and sometimes play no audio at all. MP4 is the better default; WebM exists
 * because MediaRecorder works where WebCodecs does not.
 */
export type RecordingContainer = 'video/mp4' | 'video/webm'

/**
 * The layout chosen in Settings > Video, applied when it is chosen.
 *
 * Ending a screen share does not return here: it returns to whatever layout
 * was in use when the share started, which may be one this type cannot name
 * (Auto).
 */
export type PreferredLayout = 'tiled' | 'spotlight' | 'sidebar'

type MeetingPreferencesState = {
  settingsTab: SettingsTab
  permanentBottomBar: boolean
  autoHideNames: boolean
  sideDrawerOverContent: boolean
  /** Hide the local participant tile from your own grid. In memory only, reset
   *  when a meeting ends: a saved value left people with no self tile in every
   *  later meeting. Others still see you either way. */
  hiddenSelfVideo: boolean
  /** Master switch for in-meeting toast notifications. */
  inMeetingNotifications: boolean
  /** Master switch for in-meeting sound effects. */
  inMeetingNotificationSounds: boolean
  /** When true, picture-in-picture engages automatically when the browser
   *  tab is hidden and the user is currently screen sharing. */
  autoPictureInPicture: boolean
  /** Mirror YOUR OWN view of your camera. Self-view only — remote peers always
   *  see you unmirrored, which is why this is a preference and not a transform
   *  on the published track. Set in the lobby or in Settings; one value, so the
   *  choice made before joining is the one that applies inside.
   *
   *  NOT persisted: the camera starts mirrored on every arrival. See the note
   *  in `merge`. */
  mirrorOwnVideo: boolean
  /** Container the user wants local recordings written in. */
  recordingContainer: RecordingContainer
  /** Layout picked in Settings > Video. */
  preferredLayout: PreferredLayout
  /** Suppress chat-originated toasts even when the global toasts flag is on. */
  hideChatNotifications: boolean
  /**
   * Background chosen in the lobby, before joining.
   *
   * IN MEMORY ONLY, and separate from `meetingBackground` on purpose. Two rules
   * drive this:
   *
   *  - A background is a per-session choice, not a saved setting. Persisting it
   *    meant joining a call wearing something picked days earlier, and a
   *    refresh, a leave, or a trip back to the dashboard all restored it. Not
   *    persisting is what makes those cases clear it.
   *  - Changing your background mid-meeting must not rewrite what the lobby
   *    shows. With one shared field it did, so the next prejoin preview came up
   *    wearing whatever you switched to during the last call.
   *
   * Not persisting covers a reload. The prejoin page additionally clears both
   * on MOUNT, which is what covers the in-app routes back to it — cancelling
   * out of the waiting room, or a trip through the webinar view — that no exit
   * handler was ever going to enumerate.
   */
  lobbyBackground: BackgroundImage | null
  /** Background in force inside the meeting. Seeded from `lobbyBackground` at
   *  join; changes here stay here. In memory only. */
  meetingBackground: BackgroundImage | null
  setSettingsTab: (tab: SettingsTab) => void
  setPermanentBottomBar: (v: boolean) => void
  setAutoHideNames: (v: boolean) => void
  setSideDrawerOverContent: (v: boolean) => void
  setHiddenSelfVideo: (v: boolean) => void
  setInMeetingNotifications: (v: boolean) => void
  setInMeetingNotificationSounds: (v: boolean) => void
  setAutoPictureInPicture: (v: boolean) => void
  setHideChatNotifications: (v: boolean) => void
  setLobbyBackground: (bg: BackgroundImage | null) => void
  setMeetingBackground: (bg: BackgroundImage | null) => void
  /** Carry the lobby choice into the meeting. Called once, at join. */
  applyLobbyBackgroundToMeeting: () => void
  /** Drop both. Called on leave and on landing back at the dashboard. */
  clearBackgrounds: () => void
  setMirrorOwnVideo: (mirrorOwnVideo: boolean) => void
  setRecordingContainer: (container: RecordingContainer) => void
  setPreferredLayout: (layout: PreferredLayout) => void
}

const LEGACY_SIDE_DRAWER_KEY = 'inmeet_side_drawer_over_content'
const LEGACY_BACKGROUND_KEY = 'inmeet_video_background_id'

// Hard-delete any leftover localStorage key the old prejoin code wrote so a
// stale background id can never re-apply itself across reloads. Runs once
// at module load.
if (typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem(LEGACY_BACKGROUND_KEY)
  } catch {
    // ignore — private browsing or full storage
  }
}

export const useMeetingPreferencesStore = create<MeetingPreferencesState>()(
  persist(
    (set) => ({
      settingsTab: 'audio',
      permanentBottomBar: false,
      autoHideNames: false,
      sideDrawerOverContent: false,
      hiddenSelfVideo: false,
      inMeetingNotifications: true,
      inMeetingNotificationSounds: true,
      // On, like the notifications above: switching tabs mid-meeting keeps the
      // call on screen without anyone having to find the setting first.
      autoPictureInPicture: true,
      hideChatNotifications: false,
      lobbyBackground: null,
      meetingBackground: null,
      // On: your own view starts as a mirror image, so your right hand shows on
      // the right, as in a mirror (BUG-000505). The true image read as flipped.
      // Remote peers see the true image either way.
      mirrorOwnVideo: true,
      recordingContainer: 'video/mp4',
      preferredLayout: 'tiled',
      setSettingsTab: (settingsTab) => set({ settingsTab }),
      setPermanentBottomBar: (permanentBottomBar) => set({ permanentBottomBar }),
      setAutoHideNames: (autoHideNames) => set({ autoHideNames }),
      setSideDrawerOverContent: (sideDrawerOverContent) => set({ sideDrawerOverContent }),
      setHiddenSelfVideo: (hiddenSelfVideo) => set({ hiddenSelfVideo }),
      setInMeetingNotifications: (inMeetingNotifications) => set({ inMeetingNotifications }),
      setInMeetingNotificationSounds: (inMeetingNotificationSounds) =>
        set({ inMeetingNotificationSounds }),
      setAutoPictureInPicture: (autoPictureInPicture) => set({ autoPictureInPicture }),
      setHideChatNotifications: (hideChatNotifications) => set({ hideChatNotifications }),
      setLobbyBackground: (lobbyBackground) => set({ lobbyBackground }),
      setMeetingBackground: (meetingBackground) => set({ meetingBackground }),
      applyLobbyBackgroundToMeeting: () =>
        set((s) => ({ meetingBackground: s.lobbyBackground })),
      clearBackgrounds: () => set({ lobbyBackground: null, meetingBackground: null }),
      setMirrorOwnVideo: (mirrorOwnVideo) => set({ mirrorOwnVideo }),
      setRecordingContainer: (recordingContainer) => set({ recordingContainer }),
      setPreferredLayout: (preferredLayout) => set({ preferredLayout }),
    }),
    {
      name: 'inmeet_meeting_preferences',
      // v1 stripped the background on load, v2 kept it, v3 removes it again —
      // this time as a split pair that never touches disk. v3 also EVICTS the
      // v2 value, so anyone carrying a saved background from the previous build
      // isn't stuck wearing it on their next join with no obvious way to tell
      // where it came from.
      //
      // v4 turns auto-PiP on. It used to default to off, and every save writes
      // the whole set, so nearly every browser holds `false` without anyone
      // having chosen it — a new default alone would reach almost nobody. Once
      // only: switching it off after this is kept.
      //
      // v5 stops saving hiddenSelfVideo and drops any saved value. Left on once,
      // it hid your own tile in every later meeting, and "No participants" when
      // alone, with no hint where it came from. Others could always still see you.
      version: 5,
      migrate: (persisted: unknown, version: number) => {
        if (!persisted || typeof persisted !== 'object') return persisted
        const p = persisted as Record<string, unknown>
        delete p.selectedBackgroundId
        delete p.selectedBackground
        if (version < 4) p.autoPictureInPicture = true
        delete p.hiddenSelfVideo
        return p
      },
      partialize: (state): PersistedPrefs => ({
        permanentBottomBar: state.permanentBottomBar,
        autoHideNames: state.autoHideNames,
        sideDrawerOverContent: state.sideDrawerOverContent,
        inMeetingNotifications: state.inMeetingNotifications,
        inMeetingNotificationSounds: state.inMeetingNotificationSounds,
        autoPictureInPicture: state.autoPictureInPicture,
        hideChatNotifications: state.hideChatNotifications,
        recordingContainer: state.recordingContainer,
        preferredLayout: state.preferredLayout,
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<PersistedPrefs>
        let sideDrawerOverContent = p.sideDrawerOverContent ?? current.sideDrawerOverContent
        // One-shot migration from the legacy standalone key
        if (p.sideDrawerOverContent === undefined && typeof window !== 'undefined') {
          const legacy = window.localStorage.getItem(LEGACY_SIDE_DRAWER_KEY)
          if (legacy !== null) {
            try {
              sideDrawerOverContent = JSON.parse(legacy)
            } catch {
              // ignore malformed legacy value
            }
          }
        }
        // The legacy `inmeet_video_background_id` key held only an id and is
        // still ignored — it cannot be resolved to a `BackgroundImage` here.
        return {
          ...current,
          permanentBottomBar: p.permanentBottomBar ?? current.permanentBottomBar,
          autoHideNames: p.autoHideNames ?? current.autoHideNames,
          sideDrawerOverContent,
          inMeetingNotifications:
            p.inMeetingNotifications ?? current.inMeetingNotifications,
          inMeetingNotificationSounds:
            p.inMeetingNotificationSounds ?? current.inMeetingNotificationSounds,
          autoPictureInPicture: p.autoPictureInPicture ?? current.autoPictureInPicture,
          hideChatNotifications:
            p.hideChatNotifications ?? current.hideChatNotifications,
          recordingContainer: (p.recordingContainer as RecordingContainer) ?? current.recordingContainer,
          preferredLayout: (p.preferredLayout as PreferredLayout) ?? current.preferredLayout,
          // Backgrounds are deliberately NOT restored. They live only in memory,
          // so a reload starts clean — which is the whole point.
          //
          // `mirrorOwnVideo` is the same: the camera starts MIRRORED, so your
          // right hand shows on the right, and turning that off is something
          // you ask for this time rather than something a previous visit left.
          // It is still shared in memory, so a flip chosen in the lobby carries
          // into the meeting — only the write to storage is gone. Dropping it
          // from `merge` as well as `partialize` matters: without that, an
          // install that still has an old value saved would keep coming back
          // with it for ever.
        }
      },
    },
  ),
)
